import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Box,
  ButtonBase,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import logo from '../assets/logo-mark.png';
import { colors, layout } from '../styles/tokens';

const { topBarHeight, sidebarWidth } = layout;

const AccountMenu = ({ account }) => {
  const [anchor, setAnchor] = useState(null);
  const label = account.name || account.email || 'Account';
  const close = () => setAnchor(null);

  return (
    <>
      <ButtonBase
        onClick={(event) => setAnchor(event.currentTarget)}
        aria-label="Account menu"
        sx={{ borderRadius: '10px', px: 1, py: 0.5, gap: 1, color: '#FFFFFF', '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' } }}
      >
        <Avatar sx={{ width: 32, height: 32, fontSize: 14, fontWeight: 600 }}>{label.charAt(0).toUpperCase()}</Avatar>
        <Typography noWrap sx={{ display: { xs: 'none', sm: 'block' }, maxWidth: 220, fontSize: 14, fontWeight: 500 }}>
          {label}
        </Typography>
        <ExpandMoreIcon fontSize="small" sx={{ display: { xs: 'none', sm: 'block' } }} />
      </ButtonBase>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mt: 1, minWidth: 220 } } }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          {account.name && <Typography sx={{ fontWeight: 600 }}>{account.name}</Typography>}
          {account.email && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {account.email}
            </Typography>
          )}
        </Box>
        <Divider />
        {(account.items || []).map((item) => (
          <MenuItem
            key={item.label}
            onClick={() => {
              close();
              item.onClick();
            }}
            sx={{ gap: 1.5, py: 1.25, color: item.danger ? 'error.main' : undefined }}
          >
            {item.icon}
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const SidebarNav = ({ nav, selected, onSelect, footer }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 1.5 }}>
    <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {nav.map((item) => (
        <ListItemButton
          key={item.value}
          selected={selected === item.value}
          onClick={() => onSelect(item.value)}
          sx={{ py: 1.1, fontWeight: selected === item.value ? 600 : 500 }}
        >
          {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
          <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 15, fontWeight: 'inherit' }} />
        </ListItemButton>
      ))}
    </List>
    {footer && <Box sx={{ mt: 'auto', pt: 2 }}>{footer}</Box>}
  </Box>
);

/**
 * Frame for every signed-in area: black top bar (logo, area name, extra content, account menu) and a sidebar that
 * becomes a slide-out menu on phones.
 *  - nav: [{ label, value, icon }], selected, onSelect(value)
 *  - account: { name, email, items: [{ label, onClick, icon, danger }] }
 */
const AppShell = ({ area, homeTo = '/', nav, selected, onSelect, topBarContent, account, sidebarFooter, children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const hasNav = Boolean(nav && nav.length);

  const handleSelect = (value) => {
    setMobileOpen(false);
    onSelect(value);
  };

  const sidebar = hasNav && <SidebarNav nav={nav} selected={selected} onSelect={handleSelect} footer={sidebarFooter} />;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: 'background.default' }}>
      <CssBaseline />
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar sx={{ minHeight: `${topBarHeight}px !important`, gap: { xs: 1, md: 2 }, px: { xs: 1, sm: 2, md: 3 } }}>
          {hasNav && (
            <IconButton
              color="inherit"
              aria-label="Open menu"
              onClick={() => setMobileOpen((open) => !open)}
              sx={{ display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Box
            component={RouterLink}
            to={homeTo}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#FFFFFF', textDecoration: 'none', flexShrink: 0 }}
          >
            <Box component="img" src={logo} alt="" sx={{ width: 32, height: 32 }} />
            <Typography sx={{ fontWeight: 700, fontSize: 18, letterSpacing: '0.5px', display: { xs: 'none', sm: 'block' } }}>
              VAVETEK AI
            </Typography>
          </Box>
          {area && (
            <>
              <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', my: 2, display: { xs: 'none', sm: 'block' } }} />
              <Typography noWrap sx={{ color: colors.gold, fontWeight: 600, fontSize: 16, flexShrink: 0 }}>
                {area}
              </Typography>
            </>
          )}
          <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1 }}>{topBarContent}</Box>
          {account && <AccountMenu account={account} />}
        </Toolbar>
      </AppBar>

      {hasNav && (
        <>
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': { width: sidebarWidth, top: topBarHeight, height: `calc(100% - ${topBarHeight}px)` },
            }}
          >
            {sidebar}
          </Drawer>
          <Box
            component="nav"
            sx={{
              display: { xs: 'none', md: 'block' },
              width: sidebarWidth,
              flexShrink: 0,
              position: 'sticky',
              top: topBarHeight,
              mt: `${topBarHeight}px`,
              height: `calc(100vh - ${topBarHeight}px)`,
              backgroundColor: 'background.paper',
              borderRight: `1px solid ${colors.border}`,
              overflowY: 'auto',
            }}
          >
            {sidebar}
          </Box>
        </>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          mt: `${topBarHeight}px`,
          p: { xs: 2, sm: 3, md: 4 },
          textAlign: 'left',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default AppShell;
